import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/ui/Toast';
import { agentsApi } from '../api/agentsApi';
import { agentsKeys } from '../queryKeys';
import type { AgentTaskWithdrawalKind } from '../types';

export function useAgentActions(locationId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  // A human action (approve/reject) makes Sam transition the task server-side, but that
  // transition is asynchronous: agent-suite enqueues it (e.g. reply re-queues the task to `ready`)
  // and returns 200 before it lands. A single immediate refetch races ahead of the transition and
  // still sees the task parked in `awaiting_approval`, so useAgentTasks — which only polls while a
  // task is `ready`/`in_progress` — never resumes polling and the UI looks frozen until a manual
  // reload. Re-check a few times over a short window; once a refetch observes the now-active task,
  // useAgentTasks takes over with its own 3s polling.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: agentsKeys.tasks(locationId) });
    for (const ms of [1500, 4000]) {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: agentsKeys.tasks(locationId) });
      }, ms);
    }
  };

  const approve = useMutation({
    mutationFn: (taskId: string) => agentsApi.approve(taskId, locationId),
    onSuccess: () => {
      toast.success('Approved');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to approve'),
  });

  const reject = useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason?: string }) =>
      agentsApi.reject(taskId, locationId, reason),
    onSuccess: () => {
      toast.success('Rejected');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to reject'),
  });

  // "Mark reviewed" clears a read-only audit, or failed work, to History. The transition is synchronous server-side
  // (unlike approve/reject's async re-queue), but reusing `invalidate` is harmless and keeps the list
  // in sync the same way.
  const review = useMutation({
    mutationFn: (taskId: string) => agentsApi.reviewTask(taskId, locationId),
    onSuccess: () => {
      toast.success('Marked reviewed');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to mark reviewed'),
  });

  // Publishing runs in the background after approval, so the result shows up on a later refetch.
  const retryPublish = useMutation({
    mutationFn: (taskId: string) => agentsApi.retryPublish(taskId, locationId),
    onSuccess: () => {
      toast.success('Publishing again');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to retry publishing'),
  });

  // Skipping settles the slot with no replacement, so there is nothing to wait for.
  const skip = useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason?: string }) =>
      agentsApi.skip(taskId, locationId, reason),
    onSuccess: () => {
      toast.success('Skipped');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to skip'),
  });

  // Cancelling queued work closes it with no replacement, so there is nothing to wait for either.
  const cancel = useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason?: string }) =>
      agentsApi.cancel(taskId, locationId, reason),
    onSuccess: () => {
      toast.success('Cancelled');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to cancel'),
  });

  // Agency withdrawal closes the work at once (no replacement, nothing published).
  const withdraw = useMutation({
    mutationFn: ({
      taskId,
      kind,
      reason,
    }: {
      taskId: string;
      kind: AgentTaskWithdrawalKind;
      reason?: string;
    }) => agentsApi.withdraw(taskId, locationId, kind, reason),
    onSuccess: () => {
      toast.success('Withdrawn');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to withdraw'),
  });

  return { approve, reject, review, retryPublish, skip, cancel, withdraw };
}
